export const WAITLIST_ANNOUNCEMENT_EVENT = 'waitlist-announcement';
export const WAITLIST_ANNOUNCEMENT_STORAGE_KEY = 'waitlistAnnouncementEvent';

let hasSpeechWarmupRun = false;

const formatTokenNumber = (tokenNo) => String(tokenNo || 0).padStart(3, '0');

const getSpeechEngine = () => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
        return null;
    }

    return window.speechSynthesis;
};

const getAnnouncementTableLabel = (entry = {}) => (
    String(entry.assignedTableLabel || entry.preferredTableLabel || entry.tableLabel || '').trim()
);

const getMatchingVoice = (voices, languageCodes) => {
    for (const languageCode of languageCodes) {
        const normalizedLanguage = String(languageCode || '').toLowerCase();
        const exactMatch = voices.find((voice) => String(voice.lang || '').toLowerCase() === normalizedLanguage);

        if (exactMatch) {
            return exactMatch;
        }

        const prefix = normalizedLanguage.split('-')[0];
        const prefixMatch = voices.find((voice) => String(voice.lang || '').toLowerCase().startsWith(prefix));

        if (prefixMatch) {
            return prefixMatch;
        }
    }

    return null;
};

const speakAnnouncementText = (speechEngine, voices, text, languageCodes) => {
    if (!speechEngine || !text || typeof window === 'undefined' || typeof window.SpeechSynthesisUtterance !== 'function') {
        return;
    }

    const utterance = new window.SpeechSynthesisUtterance(text);
    const matchingVoice = getMatchingVoice(voices, languageCodes);

    if (matchingVoice) {
        utterance.voice = matchingVoice;
        utterance.lang = matchingVoice.lang;
    } else if (languageCodes[0]) {
        utterance.lang = languageCodes[0];
    }

    utterance.rate = 0.95;
    utterance.pitch = 1;
    utterance.volume = 1;
    speechEngine.speak(utterance);
};

export const normalizeQueueDate = (value) => {
    if (!value) {
        return '';
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 10);
    }

    const rawValue = String(value).trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
        return rawValue;
    }

    const parsedValue = new Date(rawValue);

    if (!Number.isNaN(parsedValue.getTime())) {
        return parsedValue.toISOString().slice(0, 10);
    }

    return rawValue.slice(0, 10);
};

export const getTodayQueueDate = () => normalizeQueueDate(new Date());

export const buildWaitlistAnnouncementPayload = (entry = {}) => ({
    eventId: `${entry.waitlistID || 'waitlist'}-${entry.tokenNo || 'token'}-${Date.now()}`,
    waitlistID: entry.waitlistID || null,
    tokenNo: entry.tokenNo || 0,
    customerName: entry.customerName || '',
    assignedTableLabel: entry.assignedTableLabel || null,
    preferredTableLabel: entry.preferredTableLabel || null,
    tableLabel: getAnnouncementTableLabel(entry),
    createdAt: new Date().toISOString()
});

export const warmUpWaitlistAnnouncements = () => {
    if (hasSpeechWarmupRun) {
        return;
    }

    const speechEngine = getSpeechEngine();

    if (!speechEngine || typeof window === 'undefined' || typeof window.SpeechSynthesisUtterance !== 'function') {
        return;
    }

    try {
        const utterance = new window.SpeechSynthesisUtterance(' ');
        utterance.volume = 0;
        speechEngine.speak(utterance);
        window.setTimeout(() => {
            speechEngine.cancel();
        }, 50);
        hasSpeechWarmupRun = true;
    } catch (error) {
        // Ignore warm-up failures. The browser may still allow direct announcements later.
    }
};

export const playWaitlistAnnouncement = (payload) => {
    const speechEngine = getSpeechEngine();

    if (!speechEngine || !payload) {
        return false;
    }

    const voices = speechEngine.getVoices() || [];
    const tokenNumber = formatTokenNumber(payload.tokenNo);
    const tableLabel = getAnnouncementTableLabel(payload);
    const englishText = tableLabel
        ? `Token ${tokenNumber}, please proceed to table ${tableLabel}.`
        : `Token ${tokenNumber}, please proceed for your table.`;
    const urduText = tableLabel
        ? `ٹوکن ${tokenNumber}، براہ کرم ٹیبل ${tableLabel} پر تشریف لائیں۔`
        : `ٹوکن ${tokenNumber}، براہ کرم اپنی ٹیبل کے لیے تشریف لائیں۔`;

    try {
        speechEngine.resume();
        speakAnnouncementText(speechEngine, voices, englishText, ['en-PK', 'en-US', 'en-GB']);
        speakAnnouncementText(speechEngine, voices, urduText, ['ur-PK', 'ur-IN']);
        return true;
    } catch (error) {
        return false;
    }
};

export const broadcastWaitlistAnnouncement = (payload) => {
    if (typeof window === 'undefined' || !payload) {
        return;
    }

    const normalizedPayload = {
        ...payload,
        eventId: payload.eventId || `${payload.waitlistID || 'waitlist'}-${Date.now()}`
    };

    window.dispatchEvent(new CustomEvent(WAITLIST_ANNOUNCEMENT_EVENT, {
        detail: normalizedPayload
    }));

    try {
        localStorage.setItem(WAITLIST_ANNOUNCEMENT_STORAGE_KEY, JSON.stringify(normalizedPayload));
    } catch (error) {
        // Ignore localStorage failures and keep same-window dispatch working.
    }
};